import type { BlockInstance, CmsPage as CmsContent } from '../../cms/types'
import { normalizeStorefrontBlockTree } from '../../cms/blockContract'
import { normalizeShopLocale } from '../../i18n/shopLocale'

const ensureUniqueSiblingBlockIds = (blocks: BlockInstance[]): BlockInstance[] => {
  const seen = new Map<string, number>()
  return blocks.map((block) => {
    const seenCount = seen.get(block.id) ?? 0
    seen.set(block.id, seenCount + 1)
    const children = block.children ? ensureUniqueSiblingBlockIds(block.children) : undefined
    const nextBlock = children ? { ...block, children } : block
    if (seenCount === 0) return nextBlock
    return {
      ...nextBlock,
      id: `${block.id}__${seenCount}`,
    }
  })
}

export const normalizePage = (raw: Record<string, unknown>): CmsContent => {
  const content: BlockInstance[] = ensureUniqueSiblingBlockIds(
    normalizeStorefrontBlockTree(
      Array.isArray((raw as { content?: unknown[] }).content)
        ? (raw as { content: unknown[] }).content
        : Array.isArray((raw as { layout?: unknown[] }).layout)
          ? (raw as { layout: unknown[] }).layout
          : Array.isArray((raw as { blocks?: unknown[] }).blocks)
            ? (raw as { blocks: unknown[] }).blocks
            : [],
    ),
  )

  return {
    _id: typeof raw._id === 'string' && raw._id.trim() ? raw._id.trim() : undefined,
    title: String(raw.title ?? 'Pagina'),
    slug: String(raw.slug ?? ''),
    locale: raw.locale === 'en-US' ? 'en-US' : 'it-IT',
    translationGroupId:
      typeof raw.translationGroupId === 'string' && raw.translationGroupId.trim()
        ? raw.translationGroupId.trim()
        : `grp-${String(raw.slug ?? 'page')}`,
    translationSourcePageId:
      typeof raw.translationSourcePageId === 'string' && raw.translationSourcePageId.trim()
        ? raw.translationSourcePageId.trim()
        : null,
    translationStatus:
      raw.translationStatus === 'in_progress'
        ? 'in_progress'
        : raw.translationStatus === 'published'
        ? 'published'
        : 'draft',
    status: (raw.status as CmsContent['status']) === 'published' ? 'published' : 'draft',
    seoCanonicalLocale: normalizeShopLocale(raw.seoCanonicalLocale) ?? undefined,
    seoAlternates: Array.isArray(raw.seoAlternates)
      ? raw.seoAlternates
          .map((entry) => {
            if (!entry || typeof entry !== 'object') return null
            const candidate = entry as Record<string, unknown>
            const locale = normalizeShopLocale(candidate.locale)
            const path = typeof candidate.path === 'string' ? candidate.path.trim() : ''
            if (!locale || !path) return null
            return { locale, path }
          })
          .filter((entry): entry is NonNullable<CmsContent['seoAlternates']>[number] => Boolean(entry))
      : undefined,
    seoFallbackFromLocale: normalizeShopLocale(raw.seoFallbackFromLocale) ?? null,
    content,
    excerpt: (raw.excerpt as string | null | undefined) ?? null,
    metaTitle: (raw.metaTitle as string | null | undefined) ?? null,
    metaDescription: (raw.metaDescription as string | null | undefined) ?? null,
  }
}
